import { G } from '@mobily/ts-belt';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect } from 'react';
import {
  BooleanInput,
  ChipField,
  Form,
  NumberInput,
  required,
  useDataProvider,
  useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';

import {
  COMPANY_ID_ALLOWING_CSP_CODE,
  EMPLOYEES_BULK_CREATE_FILE_VERSION,
} from '@Common/constants/b2b/employees';
import { DOCUMENTS_UPLOAD_XLSM_MAX_SIZE } from '@Common/constants/documents';
import { GetB2bContractsEndpoint } from '@Common/types/bo/b2b/contracts';
import { GetB2bCompaniesPlansEndpoint } from '@Common/types/bo/b2b/plans';

import { DialogTitleWithX, FileInput, SaveButton } from '@sh4res/bo-common/components';
import { downloadBulkUploadFile } from '@sh4res/bo-common/components/B2bEmployee/bulkExport';
import { TemplateDownloadLinks } from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/components/TemplateDownloadLinks';
import TemplateWithMacroPath from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/template_with_macro.xlsm';
import TemplateWithMacroMlkPath from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/template_with_macro_mlk.xlsm';
import TemplateWithoutMacroPath from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/template_without_macro.xlsm';
import TemplateWithoutMacroMlkPath from '@sh4res/bo-common/components/B2bEmployeeBulkCreate/template_without_macro_mlk.xlsm';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';
import { getBrandedFile } from '@sh4res/bo-common/theme';
import { isBoExternal } from '@sh4res/bo-common/utils/env';

interface Props {
  save: (data: Record<string, unknown>) => void;
  close: () => void;
  companyId?: number;
  errorMessage?: string | null;
}

const testId = 'BoExternalEmployeeBulkCreateUploadForm';

const UploadForm = (props: Props) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();
  const { setValue, getValues } = useFormContext();
  const shouldHideCSPCodeColumn: boolean | null = useWatch({
    name: 'should_hide_csp_code_column',
    defaultValue: null,
  });

  const handleCompanyId = useCallback(
    async (value?: string) => {
      const companyId = value ?? getValues('company_id');
      if (G.isNullable(companyId)) {
        return;
      }
      const { data: contracts } = await dataProvider.getList<
        GetB2bContractsEndpoint['Client']['ResponseBody'][number]
      >(`${resourceBasePath}contracts`, {
        filter: { b2b_company_id: parseInt(companyId) },
        sort: { field: 'created_at', order: 'DESC' },
        pagination: { page: 1, perPage: 100 },
      });

      if (contracts.length === 0) {
        setValue('should_hide_csp_code_column', true);
        return;
      }

      const { data: plans } = await dataProvider.getList<
        GetB2bCompaniesPlansEndpoint['Client']['ResponseBody'][number]
      >(`${resourceBasePath}companies/plans`, {
        filter: { b2b_contract_id: contracts.map((contract) => contract.id) },
        sort: { field: 'created_at', order: 'DESC' },
        pagination: { page: 1, perPage: 100 },
      });

      const isCompanyWithCspCode = COMPANY_ID_ALLOWING_CSP_CODE.includes(parseInt(companyId));

      const plansHaveCSPCodeRule =
        isCompanyWithCspCode ||
        plans.some((plan) => plan.matching_contribution_config?.type.includes('csp_code'));

      setValue('should_hide_csp_code_column', !plansHaveCSPCodeRule);
    },
    [setValue, getValues, dataProvider, resourceBasePath],
  );

  useEffect(() => {
    if (G.isNullable(shouldHideCSPCodeColumn) && G.isNotNullable(props.companyId)) {
      handleCompanyId(props.companyId.toString());
    }
  }, [shouldHideCSPCodeColumn, props.companyId, handleCompanyId]);

  return (
    <DialogContent>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        <Stack direction="row" spacing={2}>
          <Box>
            <li style={{ margin: 0, padding: 0, marginBottom: '2rem' }}>
              <Typography variant="subtitle2" paddingBottom={1}>
                1. {translate('resources.bo_common.b2b.employees.actions.bulk_create.header_1')}
              </Typography>
              <TemplateDownloadLinks
                onDownloadWithMacro={async () =>
                  downloadBulkUploadFile({
                    file: getBrandedFile(TemplateWithMacroPath, TemplateWithMacroMlkPath),
                    exportFileName: `Fichier d'import en masse v${EMPLOYEES_BULK_CREATE_FILE_VERSION}`,
                    shouldHideCSPCode: shouldHideCSPCodeColumn ?? true,
                  })
                }
                onDownloadWithoutMacro={async () =>
                  downloadBulkUploadFile({
                    file: getBrandedFile(TemplateWithoutMacroPath, TemplateWithoutMacroMlkPath),
                    exportFileName: `Fichier d'import en masse v${EMPLOYEES_BULK_CREATE_FILE_VERSION}`,
                    shouldHideCSPCode: shouldHideCSPCodeColumn ?? true,
                  })
                }
              />
            </li>
            <li style={{ margin: 0, padding: 0 }}>
              <Typography variant="subtitle2" paddingBottom={1}>
                2. {translate('resources.bo_common.b2b.employees.actions.bulk_create.header_2')}
              </Typography>
              <Typography variant="body2">
                {translate('resources.bo_common.b2b.employees.actions.bulk_create.description')}
              </Typography>
              <br />
              {!isBoExternal() && (
                <>
                  <Typography variant="subtitle2" paddingBottom={1}>
                    {translate(
                      'resources.bo_common.b2b.employees.actions.bulk_create.option_title',
                    )}
                  </Typography>
                  <BooleanInput
                    source="automatic_onboarding"
                    label="resources.bo_common.b2b.employees.fields.automatic_onboarding"
                    defaultValue={true}
                    data-testid={`${testId}_AutomaticOnboardingInput`}
                  />
                  <Typography variant="body2">
                    {translate(
                      'resources.bo_common.b2b.employees.actions.bulk_create.automatic_onboarding_description',
                    )}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {translate(
                      'resources.bo_common.b2b.employees.actions.bulk_create.automatic_onboarding_standard_contracts_note',
                    )}
                  </Typography>
                </>
              )}
            </li>
          </Box>
          <Box width="50%">
            <li style={{ margin: 0, padding: 0 }}>
              <Typography variant="subtitle2" paddingBottom={1}>
                3. {translate('resources.bo_common.b2b.employees.actions.bulk_create.header_3')}
              </Typography>
              {G.isNullable(props.companyId) && (
                <NumberInput
                  margin="none"
                  source="company_id"
                  validate={[required()]}
                  onBlur={handleCompanyId}
                  isRequired
                  label="resources.bo_common.b2b.employees.actions.bulk_create.fields.company_id"
                  data-testid={`${testId}_CompanyIdInput`}
                />
              )}
              <FileInput
                source="file"
                label="resources.bo_common.b2b.employees.actions.bulk_create.fields.file"
                accept=".xlsm,.xlsx"
                maxSize={DOCUMENTS_UPLOAD_XLSM_MAX_SIZE}
                validate={required()}
                data-testid={`${testId}_FileInput`}>
                <ChipField source="title" data-testid="BoExternalUpload_FileChipField" />
              </FileInput>
              {props.errorMessage && (
                <Typography color="error.main">{props.errorMessage}</Typography>
              )}
            </li>
          </Box>
        </Stack>
      </ol>
    </DialogContent>
  );
};

export function Upload(props: Props) {
  const translate = useTranslate();
  const resourceBasePath = useResourceBasePath();

  return (
    <>
      <DialogTitleWithX onClose={props.close}>
        {translate('resources.bo_common.b2b.employees.actions.bulk_create.sections.upload.title')}
      </DialogTitleWithX>
      <Form onSubmit={props.save}>
        <UploadForm {...props} />
        <DialogActions>
          <SaveButton
            color="primary"
            permissions={{
              resource: `${resourceBasePath}employees/bulk_create`,
              action: 'create',
            }}
            icon={<></>}
            label="resources.bo_common.b2b.employees.actions.bulk_create.save"
            testId={`${testId}_SaveButton`}
          />
        </DialogActions>
      </Form>
    </>
  );
}
